import React from 'react';
import type { FC } from 'react';
import kebabCase from 'lodash/kebabCase.js';
import { TextInput, ConfirmInput, Box } from '@wix/cli-ui-kit';
import { getSiteWidgetExtensionsDir } from '@wix/cli-astro-definitions';
import { useWizardStep } from '../Wizard.js';
import {
  composeValidations,
  createCustomElementFolderValidation,
  useCustomElementNameValidation,
  useValidation,
  uuidValidation,
} from '../../../dev-center/components/validations/index.js';
import { useWixCliAstroBi } from '../../../bi/index.js';
import { useProjectModel } from '../../../model.js';

export const ChooseElementNameStep: FC<{
  elementName?: string;
  onSubmit: (value: string) => void;
}> = ({ onSubmit, elementName }) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();
  const validateName = useCustomElementNameValidation();

  return (
    <TextInput
      isActive={isActive}
      label={'Element name:'}
      placeholder={elementName ?? 'My Element'}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: 'Element name:',
          questionKey: 'generate_command.custom_element.choose_name',
          answer: value,
          additionalInfo: { extensionType: 'custom element' },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateName}
    />
  );
};

export const ChooseElementFolderStep: FC<{
  elementName?: string;
  elementFolder?: string;
  onSubmit: (elementFolder: string) => void;
}> = ({ onSubmit, elementFolder, elementName }) => {
  const { model } = useProjectModel();
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();
  const defaultElementFolder = kebabCase(elementName);
  const validateElementFolder = useValidation(
    createCustomElementFolderValidation(
      getSiteWidgetExtensionsDir(model.projectFolder)
    )
  );

  return (
    <TextInput
      isActive={isActive}
      label={'Enter the folder for the new element:'}
      placeholder={elementFolder ?? defaultElementFolder}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: 'Enter the folder for the new element:',
          questionKey: 'generate_command.custom_element.choose_folder',
          answer: value,
          additionalInfo: { extensionType: 'custom element' },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validateElementFolder}
    />
  );
};

export const LinkDashboardPageStep: FC<{
  onSubmit: (value: boolean) => void;
}> = ({ onSubmit }) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();

  return (
    <Box marginBottom={1}>
      <ConfirmInput
        isActive={isActive}
        label={'Link dashboard page to custom element?'}
        onSubmit={(shouldLinkDashboardPage) => {
          bi.cliFlowStepAnswered({
            question: 'Link dashboard page to custom element?',
            questionKey: 'generate_command.custom_element.link_dashboard_page',
            answer: String(shouldLinkDashboardPage),
            additionalInfo: { extensionType: 'custom element' },
          });

          onSubmit(shouldLinkDashboardPage);
          goToNextStep();
        }}
      />
    </Box>
  );
};

export const ChooseDashboardPageStep: FC<{
  onSubmit: (value: string) => void;
}> = ({ onSubmit }) => {
  const bi = useWixCliAstroBi();
  const { isActive, goToNextStep } = useWizardStep();

  const validatePageId = useValidation(
    composeValidations([
      [uuidValidation, 'Page ID has to be in the format of a UUID'],
    ])
  );

  return (
    <TextInput
      isActive={isActive}
      label={'Dashboard page id:'}
      onSubmit={(value) => {
        bi.cliFlowStepAnswered({
          question: 'Dashboard page id:',
          questionKey:
            'generate_command.custom_element.choose_dashboard_page_id',
          answer: value,
          additionalInfo: { extensionType: 'custom element' },
        });
        onSubmit(value);
        goToNextStep();
      }}
      validate={validatePageId}
    />
  );
};
